// Super admin → Gift & Promo (`super_admin/HqGiftPromo.jsx`): the platform's promo codes
// and gift cards, spent against a practice's Solunary subscription
// (docs/payment-module/platform-offers-api-contract.md §4).
//
// The shared vocabulary (a promo's terms, redemption statuses and reasons, refusal codes)
// lives in common/platformOffers.js, because the signup page and the Billing page say the
// same things. This fragment is the operator's screen only.
export default {
  fr: {
    "gp.title": "Promos & cartes cadeaux",
    "gp.subtitle": "Codes émis par Solunary, utilisables sur l'abonnement d'un cabinet.",
    "gp.tabPromos": "Promos",
    "gp.tabGifts": "Cartes cadeaux",
    "gp.pagerInfo": "{from}–{to} sur {total}",
    "gp.pagerPrev": "Précédent",
    "gp.pagerNext": "Suivant",
    "gp.error": "L'opération n'a pas abouti.",
    "gp.cancel": "Annuler",
    "gp.close": "Fermer",
    "gp.save": "Enregistrer",
    "gp.create": "Créer",
    "gp.edit": "Modifier",
    "gp.redemptions": "Utilisations",
    "gp.never": "Jamais",
    "gp.demo": "démo",
    "gp.newPromo": "Nouvelle promo",
    "gp.editPromo": "Modifier {code}",
    "gp.termsLocked": "La remise, la devise et la durée sont figées à la création (le coupon Stripe ne peut pas être modifié). Pour changer les conditions, archivez ce code et créez-en un autre.",
    "gp.promoCreated": "Promo créée",
    "gp.promoUpdated": "Promo mise à jour",
    "gp.promoArchived": "Promo archivée : elle n'est plus utilisable.",
    "gp.promoReactivated": "Promo réactivée",
    "gp.colCode": "Code",
    "gp.colDiscount": "Remise",
    "gp.colPlans": "Forfaits",
    "gp.colUses": "Utilisations",
    "gp.colExpires": "Expire le",
    "gp.colStatus": "Statut",
    "gp.allPlans": "Tous les forfaits",
    "gp.active": "active",
    "gp.inactive": "archivée",
    "gp.emptyPromo": "Aucune promo.",
    "gp.fieldCode": "Code",
    "gp.phCode": "CODE",
    "gp.fieldType": "Type de remise",
    "gp.percentOff": "Pourcentage",
    "gp.amountOff": "Montant fixe",
    "gp.fieldPercent": "Remise (%)",
    "gp.fieldAmount": "Montant",
    "gp.fieldCurrency": "Devise",
    "gp.fieldDuration": "Durée",
    "gp.duration.once": "Une seule facture",
    "gp.duration.repeating": "Plusieurs mois",
    "gp.duration.forever": "Toujours",
    "gp.fieldMonths": "Nombre de mois payés",
    "gp.fieldMaxUses": "Utilisations maximum",
    "gp.maxUsesHint": "Vide = illimité.",
    "gp.maxUsesHintUsed": "Vide = illimité. Déjà utilisé {used} fois : impossible de descendre en dessous.",
    "gp.fieldExpires": "Expire le",
    "gp.expiresHint": "Vide = sans date limite.",
    "gp.fieldPlans": "Forfaits concernés",
    "gp.plansHint": "Aucune sélection = tous les forfaits.",
    "gp.plansLoadFailed": "Les forfaits n'ont pas pu être chargés : le code s'appliquera à tous les forfaits.",
    "gp.plansUnavailable": "Votre rôle ne permet pas de lire la liste des forfaits : le code s'appliquera à tous les forfaits.",
    "gp.fieldDescription": "Note interne",
    "gp.phDescription": "Pour qui, pourquoi…",
    "gp.valid.code": "Le code doit faire 2 à 64 caractères : lettres, chiffres, « - » ou « _ ».",
    "gp.valid.codeGiftPrefix": "Un code promo ne peut pas commencer par « GC- », réservé aux cartes cadeaux.",
    "gp.valid.percent": "Indiquez un pourcentage entre 0,01 et 100, avec au plus deux décimales.",
    "gp.valid.amount": "Indiquez un montant positif, avec au plus deux décimales.",
    "gp.valid.months": "Indiquez un nombre de mois entre 1 et 36.",
    "gp.valid.maxUses": "Le nombre d'utilisations doit être un entier positif, ou vide.",
    "gp.valid.expiresPast": "La date d'expiration doit être dans le futur.",
    "gp.valid.giftAmount": "Le montant d'une carte cadeau va de {min} à {max}, avec au plus deux décimales.",
    "gp.newGift": "Nouvelle carte cadeau",
    "gp.emptyGift": "Aucune carte cadeau.",
    "gp.giftCreated": "Carte cadeau créée et envoyée",
    "gp.giftCreatedUndelivered": "Carte cadeau créée, mais l'e-mail n'a pas pu être envoyé. Utilisez « Renvoyer ».",
    "gp.giftResent": "E-mail renvoyé",
    "gp.giftDeliveryFailed": "L'e-mail n'a pas pu être envoyé. Réessayez plus tard.",
    "gp.giftVoided": "Carte cadeau annulée",
    "gp.colAmount": "Montant",
    "gp.colBalance": "Solde",
    "gp.colRecipient": "Destinataire",
    "gp.colDelivery": "Envoi",
    "gp.colCreated": "Créé",
    "gp.colMessage": "Message",
    "gp.noMessage": "Aucun message",
    "gp.createdByUnknown": "Auteur inconnu",
    "gp.giftStatus.active": "active",
    "gp.giftStatus.held": "réservée",
    "gp.giftStatus.redeemed": "utilisée",
    "gp.giftStatus.voided": "annulée",
    "gp.delivered": "envoyée",
    "gp.notDelivered": "non envoyée",
    "gp.attempts": "{n} tentative(s)",
    "gp.resend": "Renvoyer l'e-mail",
    "gp.void": "Annuler la carte",
    "gp.fieldRecipientEmail": "E-mail du destinataire",
    "gp.phRecipientEmail": "nom@exemple.com",
    "gp.fieldRecipientName": "Nom du destinataire",
    "gp.phRecipientName": "Facultatif",
    "gp.fieldLocale": "Langue de l'e-mail",
    "gp.localeHint": "La langue dans laquelle le destinataire reçoit sa carte.",
    "gp.locale.fr": "Français",
    "gp.locale.en": "Anglais",
    "gp.locale.es": "Espagnol",
    "gp.fieldMessage": "Message",
    "gp.phMessage": "Un mot pour le destinataire (facultatif)",
    "gp.createAndSend": "Créer et envoyer",
    "gp.voidTitle": "Annuler {code} ?",
    "gp.voidBody": "La carte ne pourra plus être utilisée. Cette action est définitive.",
    "gp.voidReasonPlaceholder": "Motif (3 caractères minimum)",
    "gp.voidConfirm": "Annuler la carte",
    "gp.redemptionsTitle": "Utilisations de {code}",
    "gp.redemptionsEmpty": "Ce code n'a encore été utilisé par aucun cabinet.",
    "gp.redemptionDeletedPractice": "{name} (cabinet supprimé)",
    "gp.heldAt": "réservé le {date}",
    "gp.appliedAt": "appliqué le {date}",
    "gp.releasedAt": "libéré le {date}",
  },
  en: {
    "gp.title": "Promos & gift cards",
    "gp.subtitle": "Codes issued by Solunary, redeemable against a practice's subscription.",
    "gp.tabPromos": "Promos",
    "gp.tabGifts": "Gift cards",
    "gp.pagerInfo": "{from}–{to} of {total}",
    "gp.pagerPrev": "Previous",
    "gp.pagerNext": "Next",
    "gp.error": "The operation did not go through.",
    "gp.cancel": "Cancel",
    "gp.close": "Close",
    "gp.save": "Save",
    "gp.create": "Create",
    "gp.edit": "Edit",
    "gp.redemptions": "Redemptions",
    "gp.never": "Never",
    "gp.demo": "demo",
    "gp.newPromo": "New promo",
    "gp.editPromo": "Edit {code}",
    "gp.termsLocked": "The discount, currency and duration are fixed at creation (the Stripe coupon cannot be changed). To change the terms, archive this code and create a new one.",
    "gp.promoCreated": "Promo created",
    "gp.promoUpdated": "Promo updated",
    "gp.promoArchived": "Promo archived: it can no longer be used.",
    "gp.promoReactivated": "Promo reactivated",
    "gp.colCode": "Code",
    "gp.colDiscount": "Discount",
    "gp.colPlans": "Plans",
    "gp.colUses": "Uses",
    "gp.colExpires": "Expires",
    "gp.colStatus": "Status",
    "gp.allPlans": "All plans",
    "gp.active": "active",
    "gp.inactive": "archived",
    "gp.emptyPromo": "No promos.",
    "gp.fieldCode": "Code",
    "gp.phCode": "CODE",
    "gp.fieldType": "Discount type",
    "gp.percentOff": "Percentage",
    "gp.amountOff": "Fixed amount",
    "gp.fieldPercent": "Discount (%)",
    "gp.fieldAmount": "Amount",
    "gp.fieldCurrency": "Currency",
    "gp.fieldDuration": "Duration",
    "gp.duration.once": "One invoice",
    "gp.duration.repeating": "Several months",
    "gp.duration.forever": "Forever",
    "gp.fieldMonths": "Number of paid months",
    "gp.fieldMaxUses": "Maximum uses",
    "gp.maxUsesHint": "Blank = unlimited.",
    "gp.maxUsesHintUsed": "Blank = unlimited. Already used {used} times: it cannot go below that.",
    "gp.fieldExpires": "Expires",
    "gp.expiresHint": "Blank = no end date.",
    "gp.fieldPlans": "Eligible plans",
    "gp.plansHint": "No selection = every plan.",
    "gp.plansLoadFailed": "The plans could not be loaded: the code will apply to every plan.",
    "gp.plansUnavailable": "Your role cannot read the plan list: the code will apply to every plan.",
    "gp.fieldDescription": "Internal note",
    "gp.phDescription": "Who it is for, and why…",
    "gp.valid.code": "The code must be 2 to 64 characters: letters, digits, \"-\" or \"_\".",
    "gp.valid.codeGiftPrefix": "A promo code cannot start with \"GC-\", which is reserved for gift cards.",
    "gp.valid.percent": "Enter a percentage between 0.01 and 100, with at most two decimals.",
    "gp.valid.amount": "Enter a positive amount, with at most two decimals.",
    "gp.valid.months": "Enter a number of months between 1 and 36.",
    "gp.valid.maxUses": "Maximum uses must be a positive whole number, or blank.",
    "gp.valid.expiresPast": "The expiry date must be in the future.",
    "gp.valid.giftAmount": "A gift card is worth {min} to {max}, with at most two decimals.",
    "gp.newGift": "New gift card",
    "gp.emptyGift": "No gift cards.",
    "gp.giftCreated": "Gift card created and sent",
    "gp.giftCreatedUndelivered": "Gift card created, but the email could not be sent. Use \"Resend\".",
    "gp.giftResent": "Email resent",
    "gp.giftDeliveryFailed": "The email could not be sent. Try again later.",
    "gp.giftVoided": "Gift card voided",
    "gp.colAmount": "Amount",
    "gp.colBalance": "Balance",
    "gp.colRecipient": "Recipient",
    "gp.colDelivery": "Delivery",
    "gp.colCreated": "Created",
    "gp.colMessage": "Message",
    "gp.noMessage": "No message",
    "gp.createdByUnknown": "Unknown creator",
    "gp.giftStatus.active": "active",
    "gp.giftStatus.held": "reserved",
    "gp.giftStatus.redeemed": "redeemed",
    "gp.giftStatus.voided": "voided",
    "gp.delivered": "sent",
    "gp.notDelivered": "not sent",
    "gp.attempts": "{n} attempt(s)",
    "gp.resend": "Resend the email",
    "gp.void": "Void the card",
    "gp.fieldRecipientEmail": "Recipient email",
    "gp.phRecipientEmail": "name@example.com",
    "gp.fieldRecipientName": "Recipient name",
    "gp.phRecipientName": "Optional",
    "gp.fieldLocale": "Email language",
    "gp.localeHint": "The language the recipient receives their card in.",
    "gp.locale.fr": "French",
    "gp.locale.en": "English",
    "gp.locale.es": "Spanish",
    "gp.fieldMessage": "Message",
    "gp.phMessage": "A note for the recipient (optional)",
    "gp.createAndSend": "Create and send",
    "gp.voidTitle": "Void {code}?",
    "gp.voidBody": "The card can no longer be used. This cannot be undone.",
    "gp.voidReasonPlaceholder": "Reason (at least 3 characters)",
    "gp.voidConfirm": "Void the card",
    "gp.redemptionsTitle": "Redemptions of {code}",
    "gp.redemptionsEmpty": "No practice has used this code yet.",
    "gp.redemptionDeletedPractice": "{name} (practice deleted)",
    "gp.heldAt": "reserved {date}",
    "gp.appliedAt": "applied {date}",
    "gp.releasedAt": "released {date}",
  },
  es: {
    "gp.title": "Promociones y tarjetas regalo",
    "gp.subtitle": "Códigos emitidos por Solunary, canjeables en la suscripción de un consultorio.",
    "gp.tabPromos": "Promociones",
    "gp.tabGifts": "Tarjetas regalo",
    "gp.pagerInfo": "{from}–{to} de {total}",
    "gp.pagerPrev": "Anterior",
    "gp.pagerNext": "Siguiente",
    "gp.error": "La operación no se completó.",
    "gp.cancel": "Cancelar",
    "gp.close": "Cerrar",
    "gp.save": "Guardar",
    "gp.create": "Crear",
    "gp.edit": "Editar",
    "gp.redemptions": "Canjes",
    "gp.never": "Nunca",
    "gp.demo": "demo",
    "gp.newPromo": "Nueva promoción",
    "gp.editPromo": "Editar {code}",
    "gp.termsLocked": "El descuento, la moneda y la duración se fijan al crearla (el cupón de Stripe no se puede modificar). Para cambiar las condiciones, archive este código y cree otro.",
    "gp.promoCreated": "Promoción creada",
    "gp.promoUpdated": "Promoción actualizada",
    "gp.promoArchived": "Promoción archivada: ya no se puede usar.",
    "gp.promoReactivated": "Promoción reactivada",
    "gp.colCode": "Código",
    "gp.colDiscount": "Descuento",
    "gp.colPlans": "Planes",
    "gp.colUses": "Usos",
    "gp.colExpires": "Vence",
    "gp.colStatus": "Estado",
    "gp.allPlans": "Todos los planes",
    "gp.active": "activa",
    "gp.inactive": "archivada",
    "gp.emptyPromo": "No hay promociones.",
    "gp.fieldCode": "Código",
    "gp.phCode": "CÓDIGO",
    "gp.fieldType": "Tipo de descuento",
    "gp.percentOff": "Porcentaje",
    "gp.amountOff": "Importe fijo",
    "gp.fieldPercent": "Descuento (%)",
    "gp.fieldAmount": "Importe",
    "gp.fieldCurrency": "Moneda",
    "gp.fieldDuration": "Duración",
    "gp.duration.once": "Una sola factura",
    "gp.duration.repeating": "Varios meses",
    "gp.duration.forever": "Siempre",
    "gp.fieldMonths": "Número de meses pagados",
    "gp.fieldMaxUses": "Usos máximos",
    "gp.maxUsesHint": "Vacío = ilimitado.",
    "gp.maxUsesHintUsed": "Vacío = ilimitado. Ya se usó {used} veces: no puede bajar de ahí.",
    "gp.fieldExpires": "Vence",
    "gp.expiresHint": "Vacío = sin fecha límite.",
    "gp.fieldPlans": "Planes aplicables",
    "gp.plansHint": "Sin selección = todos los planes.",
    "gp.plansLoadFailed": "No se pudieron cargar los planes: el código se aplicará a todos.",
    "gp.plansUnavailable": "Tu rol no permite leer la lista de planes: el código se aplicará a todos.",
    "gp.fieldDescription": "Nota interna",
    "gp.phDescription": "Para quién y por qué…",
    "gp.valid.code": "El código debe tener de 2 a 64 caracteres: letras, cifras, «-» o «_».",
    "gp.valid.codeGiftPrefix": "Un código promocional no puede empezar por «GC-», reservado a las tarjetas regalo.",
    "gp.valid.percent": "Indique un porcentaje entre 0,01 y 100, con dos decimales como máximo.",
    "gp.valid.amount": "Indique un importe positivo, con dos decimales como máximo.",
    "gp.valid.months": "Indique un número de meses entre 1 y 36.",
    "gp.valid.maxUses": "Los usos máximos deben ser un número entero positivo, o quedar vacíos.",
    "gp.valid.expiresPast": "La fecha de vencimiento debe ser futura.",
    "gp.valid.giftAmount": "Una tarjeta regalo vale de {min} a {max}, con dos decimales como máximo.",
    "gp.newGift": "Nueva tarjeta regalo",
    "gp.emptyGift": "No hay tarjetas regalo.",
    "gp.giftCreated": "Tarjeta regalo creada y enviada",
    "gp.giftCreatedUndelivered": "Tarjeta regalo creada, pero el correo no se pudo enviar. Use «Reenviar».",
    "gp.giftResent": "Correo reenviado",
    "gp.giftDeliveryFailed": "El correo no se pudo enviar. Inténtelo más tarde.",
    "gp.giftVoided": "Tarjeta regalo anulada",
    "gp.colAmount": "Importe",
    "gp.colBalance": "Saldo",
    "gp.colRecipient": "Destinatario",
    "gp.colDelivery": "Envío",
    "gp.colCreated": "Creado",
    "gp.colMessage": "Mensaje",
    "gp.noMessage": "Sin mensaje",
    "gp.createdByUnknown": "Creador desconocido",
    "gp.giftStatus.active": "activa",
    "gp.giftStatus.held": "reservada",
    "gp.giftStatus.redeemed": "canjeada",
    "gp.giftStatus.voided": "anulada",
    "gp.delivered": "enviada",
    "gp.notDelivered": "no enviada",
    "gp.attempts": "{n} intento(s)",
    "gp.resend": "Reenviar el correo",
    "gp.void": "Anular la tarjeta",
    "gp.fieldRecipientEmail": "Correo del destinatario",
    "gp.phRecipientEmail": "nombre@ejemplo.com",
    "gp.fieldRecipientName": "Nombre del destinatario",
    "gp.phRecipientName": "Opcional",
    "gp.fieldLocale": "Idioma del correo",
    "gp.localeHint": "El idioma en que el destinatario recibe su tarjeta.",
    "gp.locale.fr": "Francés",
    "gp.locale.en": "Inglés",
    "gp.locale.es": "Español",
    "gp.fieldMessage": "Mensaje",
    "gp.phMessage": "Unas palabras para el destinatario (opcional)",
    "gp.createAndSend": "Crear y enviar",
    "gp.voidTitle": "¿Anular {code}?",
    "gp.voidBody": "La tarjeta ya no se podrá usar. Esta acción es definitiva.",
    "gp.voidReasonPlaceholder": "Motivo (3 caracteres como mínimo)",
    "gp.voidConfirm": "Anular la tarjeta",
    "gp.redemptionsTitle": "Canjes de {code}",
    "gp.redemptionsEmpty": "Ningún consultorio ha usado este código todavía.",
    "gp.redemptionDeletedPractice": "{name} (consulta eliminada)",
    "gp.heldAt": "reservado el {date}",
    "gp.appliedAt": "aplicado el {date}",
    "gp.releasedAt": "liberado el {date}",
  },
};
